function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
import { TermsStrings, PricingStates, IAvailabilityActions } from '@shared/Models';
import { Constants, Environment, supportedCountries as SUPPORTED_COUNTRIES, IConsentOption, allBillingCountries } from '@shared/utils/constants';
import { getWindow } from '@shared/services/window';
import { SpzaInstrumentService } from '@shared/services/telemetry/spza/spzaInstrument';
import { getFullPriceString } from './pricing';
import { logger } from '@src/logger';
import { getPrimaryProductUrl } from '@shared/utils/datamappingHelpers';
import { getPDPRoute, getProductByUrlKey } from '@shared/utils/appUtils';
import { urlReplace } from '@shared/routerHistory';
export const updateCart = ({ props, id, title, description, quantity, isQuantifiable, minQuantity, currency, maxQuantity, billingTerm, termId, isRecurring, showAutoRenewMessage, bigId, skuId, hasFreeTrial, price })=>{
    const prevCartItem = props.checkout.items[0];
    const cartItem = {
        id,
        title,
        description,
        entityId: props.app.entityId,
        appTitle: props.app.title,
        appImageUri: props.app.iconURL,
        unitPrice: {
            currency: currency || prevCartItem && prevCartItem.unitPrice.currency,
            unit: billingTerm,
            value: price
        },
        linkedAddIns: props.app.linkedAddIns
    };
    cartItem.isQuantifiable = typeof isQuantifiable !== 'undefined' ? isQuantifiable : prevCartItem === null || prevCartItem === void 0 ? void 0 : prevCartItem.isQuantifiable;
    let quantityAsInt = parseInt(quantity, 10);
    if (isNaN(quantityAsInt)) {
        quantityAsInt = prevCartItem.quantity;
    }
    cartItem.quantityString = quantity || prevCartItem.quantityString;
    cartItem.quantity = quantityAsInt;
    cartItem.minQuantity = minQuantity || (prevCartItem === null || prevCartItem === void 0 ? void 0 : prevCartItem.minQuantity);
    cartItem.maxQuantity = maxQuantity || (prevCartItem === null || prevCartItem === void 0 ? void 0 : prevCartItem.maxQuantity);
    cartItem.termId = termId || (prevCartItem === null || prevCartItem === void 0 ? void 0 : prevCartItem.termId);
    cartItem.termDuration = billingTerm || (prevCartItem === null || prevCartItem === void 0 ? void 0 : prevCartItem.termDuration);
    cartItem.recurringBilling = isRecurring || (prevCartItem === null || prevCartItem === void 0 ? void 0 : prevCartItem.recurringBilling);
    cartItem.showAutoRenewMessage = showAutoRenewMessage || (prevCartItem === null || prevCartItem === void 0 ? void 0 : prevCartItem.showAutoRenewMessage);
    cartItem.bigId = bigId || (prevCartItem === null || prevCartItem === void 0 ? void 0 : prevCartItem.bigId);
    cartItem.skuId = skuId || (prevCartItem === null || prevCartItem === void 0 ? void 0 : prevCartItem.skuId);
    cartItem.hasFreeTrial = hasFreeTrial;
    // No need for input as it won't change during plan update
    cartItem.product = prevCartItem === null || prevCartItem === void 0 ? void 0 : prevCartItem.product;
    // every time something changes in the cart we reset status for calculate now link,
    // this should only be done if we also have a payment instrument selected
    if (JSON.stringify(cartItem) !== JSON.stringify(prevCartItem)) {
        props.setCartItem(cartItem);
    }
};
export const isNewCustomer = ({ activeCustomer, customers })=>{
    return !activeCustomer && (customers === null || customers === void 0 ? void 0 : customers.length) === 0;
};
export const checkoutBillingCountry = ({ activeCustomer, customers, defaultBillingCountry })=>{
    var _activeCustomer_address;
    var _activeCustomer_address_country;
    const billingCountry = isNewCustomer({
        activeCustomer,
        customers
    }) ? defaultBillingCountry : (_activeCustomer_address_country = activeCustomer === null || activeCustomer === void 0 ? void 0 : (_activeCustomer_address = activeCustomer.address) === null || _activeCustomer_address === void 0 ? void 0 : _activeCustomer_address.country) !== null && _activeCustomer_address_country !== void 0 ? _activeCustomer_address_country : customers[0].address.country;
    return billingCountry.toLowerCase();
};
export function isAvailableInThisRegion({ startingPrice }) {
    return startingPrice && startingPrice.pricingData !== PricingStates.NotAvailableInThisRegion;
}
export const isDevelopmentEnvironment = (env)=>{
    return env !== Environment.Production;
};
export function getCountries() {
    const countriesList = allBillingCountries.filter((country)=>!!country.transactable).map((country)=>{
        return {
            text: country.name,
            value: country.countryCode
        };
    });
    return countriesList;
}
export function logConfirmTelemetry(countryCode) {
    const logPayload = {
        page: getWindow().location.href,
        action: Constants.Telemetry.Action.Click,
        actionModifier: Constants.Telemetry.ActionModifier.BillingCountry,
        details: `Country ${countryCode} confirmed`
    };
    SpzaInstrumentService.getProvider().probe('logTelemetryInfo', logPayload);
    logger.info(logPayload.details, {
        action: logPayload.action,
        actionModifier: logPayload.actionModifier
    });
}
export function confirmBillingCountry(_) {
    return _confirmBillingCountry.apply(this, arguments);
}
function _confirmBillingCountry() {
    _confirmBillingCountry = _async_to_generator(function*({ config, setBillingCountry, createCustomer }) {
        try {
            yield setBillingCountry(config.billingCountryCode);
            createCustomer();
        } catch (e) {
            const logPayload = {
                page: getWindow().location.href,
                action: Constants.Telemetry.Action.Click,
                actionModifier: Constants.Telemetry.ActionModifier.CreateCustomer,
                details: `Error during create customer, or setting billing country, retrying create customer.`
            };
            createCustomer();
            SpzaInstrumentService.getProvider().probe('logTelemetryInfo', logPayload);
            logger.info(logPayload.details, {
                action: logPayload.action,
                actionModifier: logPayload.actionModifier
            });
        } finally{
            logConfirmTelemetry(config.billingCountryCode);
        }
    });
    return _confirmBillingCountry.apply(this, arguments);
}
export const getConsentState = (countryCode)=>{
    const selectedCountry = SUPPORTED_COUNTRIES.find((country)=>country.countryCode.toLowerCase() === countryCode.toLowerCase());
    return selectedCountry.consentRequired;
};
/**
 * Determine if should show contact info in purchase disclaimer
 * @param consentOption The consent type to check if requires checkbox
 * @returns true if country doesn't require consent checkbox
 */ export const isContactInfoHiddenInDisclaimer = (consentOption)=>{
    const consentsNotContactInfo = [
        IConsentOption.Exclude
    ];
    return consentsNotContactInfo.includes(consentOption);
};
/**
 * These shouldn't be localized, they are used for logging purposes only.
 * @param stepId The stepId to convert back to string
 */ export const convertStepIdToName = (stepId)=>{
    const mapping = {
        0: 'Billing region picker',
        1: 'Select plan',
        2: 'Price and payment options',
        3: 'Purchase Blends screen',
        4: 'Configure your purchase'
    };
    return mapping[`${stepId}`];
};
export const getBillingPlanPricesPerTermDuration = ({ plan, billingDuration })=>{
    var _renewAvailability_terms_filter, _renewAvailability_terms;
    const renewAvailability = plan.availabilities.find((availability)=>{
        const { actions } = availability;
        return actions.includes(IAvailabilityActions.Renew) && actions.includes(IAvailabilityActions.Browse);
    });
    const prices = renewAvailability === null || renewAvailability === void 0 ? void 0 : (_renewAvailability_terms = renewAvailability.terms) === null || _renewAvailability_terms === void 0 ? void 0 : (_renewAvailability_terms_filter = _renewAvailability_terms.filter((term)=>term.termUnits === billingDuration)) === null || _renewAvailability_terms_filter === void 0 ? void 0 : _renewAvailability_terms_filter.map((term)=>({
            totalPrice: term.price,
            billingPlan: term.billingPlan,
            termDuration: term.termUnits,
            /** This is the RENEW termId in a free-trial availability-term */ termId: term.termId
        }));
    return prices || [];
};
/**
 * This method returns the full price string in the format of: "First month three, then <SomePrice+Currency>"
 * Depends on hasFree trial for first month free prefix
 * TermId should always be the renew termId with price, if it's the free trial termId, the price you'll see is 0.0 which is misleading.
 */ export const convertTermToString = ({ context, term, countryCode, isQuantifiable, hasFreeTrial, locale })=>{
    return getFullPriceString({
        context,
        countryCode,
        currency: term.price.currencyCode,
        priceValue: term.price.msrp,
        billingPlan: term.billingPlan,
        termDuration: term.renewTermUnits,
        isQuantifiable,
        hasFreeTrial,
        locale
    });
};
export const pbAnnually = 'Annually';
export const pbMonthly = 'Monthly';
export const pbOther = 'Other';
export const getBillingCycle = (term, billingPlan)=>{
    const MultiYearTerms = [
        TermsStrings.Term5YString,
        TermsStrings.Term4YString,
        TermsStrings.Term3YString,
        TermsStrings.Term2YString
    ];
    if (MultiYearTerms.includes(term) || billingPlan) {
        return pbOther;
    }
    if ([
        TermsStrings.Term1YString,
        TermsStrings.YearString
    ].includes(term)) {
        return pbAnnually;
    }
    if ([
        TermsStrings.Term1MString,
        TermsStrings.MonthString
    ].includes(term)) {
        return pbMonthly;
    }
};
export const buildUrlToAppDetails = (tab, app, buildHref)=>{
    const productString = getPrimaryProductUrl(app.primaryProduct);
    const productData = getProductByUrlKey({
        urlKey: productString
    });
    const product = productData ? productData.UrlKey : productString;
    // redirect to PDP page, Plan and Price tab
    const pdpUrl = buildHref(getPDPRoute(app.appType), {
        productId: product,
        entityId: app.entityId
    }, {
        tab
    }, true);
    return pdpUrl;
};
export const redirectToAppDetails = (tab, app, buildHref)=>{
    const pdpUrl = buildUrlToAppDetails(tab, app, buildHref);
    urlReplace(pdpUrl);
};
export const getPurchaseBlendsMessageOverrides = (context, isOneTimePayment, isProfessionalService)=>{
    const riskRejected = context.locParams('PurchaseBlends_Risk_Rejected_Error', [
        'https://go.microsoft.com/fwlink/?linkid=2101400&clcid=0x409'
    ], 'This purchase cannot be completed. Please contact Microsoft support via this link: https://go.microsoft.com/fwlink/?linkid=2101400&clcid=0x409');
    return isOneTimePayment ? {
        'component.message-center.risk_rejected_error': riskRejected,
        'component.line-item.product-details.billing_term': ' ',
        'component.line-item.product-details.recurring_billing': ' ',
        'component.line-item.recurring-billing.off': ' ',
        'component.line-item.duration.one_month': ' ',
        'component.line-item.monthly_billing_cycle_per_user': ' ',
        'component.line-item.duration.total.one_month': ' ',
        'component.common.line-item.subtotal_term_message': ' '
    } : isProfessionalService ? {
        'component.message-center.risk_rejected_error': riskRejected,
        'component.line-item.product-details.recurring_billing': ' ',
        'component.line-item.recurring-billing.off': ' '
    } : {
        'component.message-center.risk_rejected_error': riskRejected
    };
};
